'use client';

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import {
  ArrowRight,
  Check,
  Circle,
  Clock,
  Cloud,
  Compass,
  HeartPulse,
  ListChecks,
  MessageCircle,
  Moon,
  MoreHorizontal,
  Newspaper,
  PenLine,
  Route,
  Smartphone,
  Sparkles,
  Star,
  Tv,
  User,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { QuestionIcon } from '@timeblock/assessment-content/assessments/follow-through-plan';
import { cn } from '@/lib/utils';

const ICONS: Record<QuestionIcon, LucideIcon> = {
  heart: HeartPulse,
  list: ListChecks,
  compass: Compass,
  route: Route,
  person: User,
  sparkle: Sparkles,
  circle: Circle,
  clock: Clock,
  cloud: Cloud,
  zap: Zap,
  message: MessageCircle,
  pen: PenLine,
  moon: Moon,
  smartphone: Smartphone,
  newspaper: Newspaper,
  tv: Tv,
  more: MoreHorizontal,
};

/** Warm page background with a single centred column, as on Purpose's funnel. */
export function FunnelShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="min-h-screen bg-[#F4EFEA] px-4 pb-16 pt-6">
      <div className={cn('mx-auto w-full max-w-md', className)}>{children}</div>
    </div>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-2xl bg-white p-5 shadow-[0_8px_28px_rgba(42,99,84,0.08)] ring-1 ring-black/[0.08]', className)}>
      {children}
    </div>
  );
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn('text-xs font-bold uppercase tracking-[0.16em] text-[#2A6354]', className)}>{children}</p>
  );
}

export function Stars({ className }: { className?: string }) {
  return (
    <div className={cn('flex justify-center gap-0.5 text-[#E8A33D]', className)} aria-label="Five stars" role="img">
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} className="h-5 w-5 fill-current" aria-hidden="true" />
      ))}
    </div>
  );
}

interface OptionButtonProps {
  label: string;
  icon?: QuestionIcon;
  selected?: boolean;
  multiSelect?: boolean;
  onClick: () => void;
}

export function OptionButton({ label, icon, selected = false, multiSelect = false, onClick }: OptionButtonProps) {
  const Icon = icon ? ICONS[icon] : null;
  return (
    <button
      type="button"
      onClick={onClick}
      role={multiSelect ? 'checkbox' : undefined}
      aria-checked={multiSelect ? selected : undefined}
      className={cn(
        'flex min-h-[56px] w-full items-center gap-3 rounded-xl bg-white px-4 py-3 text-left text-[15px] font-medium text-gray-900 ring-1 ring-black/[0.65] transition',
        'hover:bg-[#2A6354]/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A6354]',
        selected && 'bg-[#2A6354]/[0.07] ring-2 ring-[#2A6354]'
      )}
    >
      {Icon && <Icon className="h-5 w-5 shrink-0 text-gray-600" aria-hidden="true" />}
      <span className="flex-1">{label}</span>
      {multiSelect && (
        <span
          aria-hidden="true"
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded border border-gray-400',
            selected && 'border-[#2A6354] bg-[#2A6354] text-white'
          )}
        >
          {selected && <Check className="h-3.5 w-3.5" />}
        </span>
      )}
    </button>
  );
}

interface CtaButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: 'green' | 'orange';
  arrow?: boolean;
}

/** The one button on a screen that stands out. */
export function CtaButton({ tone = 'green', arrow = false, className, children, ...props }: CtaButtonProps) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl px-6 text-lg font-bold text-white shadow-lg transition',
        'hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'green' ? 'bg-[#2A6354] shadow-[#2A6354]/25 focus-visible:ring-[#2A6354]/40' : 'bg-[#E2672A] shadow-[#E2672A]/25 focus-visible:ring-[#E2672A]/40',
        className
      )}
    >
      {children}
      {arrow && <ArrowRight className="h-5 w-5" aria-hidden="true" />}
    </button>
  );
}

export function ProgressBar({ value }: { value: number }) {
  const percent = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-black/[0.08]"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-label="Quiz progress"
    >
      <div className="h-full rounded-full bg-[#2A6354] transition-[width] duration-300" style={{ width: `${percent}%` }} />
    </div>
  );
}
